<template>
  <v-menu
    content-class="menu-wrapper"
    nudge-bottom="12"
    offset-y
    slot="right"
    :close-on-content-click="false"
  >
    <template v-slot:activator="{ on, attrs }">
      <v-btn
        class="engine-button"
        :class="{ 'offline': !isOn }"
        small
        tile
        v-bind="attrs"
        v-on="on"
      >
        <v-icon size="20">$vuetify.icons.flag</v-icon>
        <span class="mr-1">{{ isOn ? 'Online:' : 'Offline:' }}</span>
        <span class="event-name-header fw-700">{{ eventName }}</span>
      </v-btn>
    </template>
    <div class="menu-header">
      <span class="menu-title">ML Engine:</span>
      <span class="menu-event-name">{{ eventName }}</span>
      <span class="styled-cell">Turns Off when idle for more than 3 hours</span>
      <div class="option-buttons styled-cell">
        <div class="option-button-wrap stop">
          <v-btn :loading="loading" @click="handleStart">
            <v-icon>{{ isOn ? '$vuetify.icons.stop' : '$vuetify.icons.chevron-right-filled' }}</v-icon>
          </v-btn>
          {{ isOn ? 'Stop' : 'Start' }}
        </div>
        <div class="option-button-wrap reboot">
          <v-btn :disabled="!isOn" :loading="loading" @click="restartEngine">
            <v-icon size="20">$vuetify.icons.refresh</v-icon>
          </v-btn>
          Reboot
        </div>
      </div>
    </div>
    <v-divider class="divider my-1" />
    <div class="menu-row mb-1">
      <span class="fs-16">ML Engine Status:</span>
      <v-btn height="40" :loading="info.loading" @click="getInfo">
        <v-icon left>$vuetify.icons.refresh-2</v-icon>
        Refresh
      </v-btn>
    </div>
    <div class="menu-row">
      <span>Last Image:</span>
      <span class="fw-500">{{ formatDate(info.image_time) }}</span>
    </div>
    <div class="menu-row">
      <span>Queue Size:</span>
      <span class="fw-500">{{ info.queue_length }} Img.</span>
    </div>
  </v-menu>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'MlEngine',
  computed: {
    ...mapGetters ({
      info: 'engine/info',
      isOn: 'engine/on',
      loading: 'engine/loading',
    }),
    eventName() {
      return this.info.race_event || '-'
    },
  },
  methods: {
    ...mapActions({
      getInfo: 'engine/getInfo',
      getStatus: 'engine/getStatus',
      restartEngine: 'engine/restartEngine',
      startEngine: 'engine/startEngine',
      stopEngine: 'engine/stopEngine',
    }),
    formatDate(date) {
      if (!date) {
        return '-'
      }
      const dateOptions = { month: 'short', day: 'numeric', hour: 'numeric', minute: 'numeric' }
      return new Date(date).toLocaleString('en-US', dateOptions)
    },
    handleStart() {
      this.isOn ? this.stopEngine() : this.startEngine()
    },
  },
  created() {
    setTimeout(() => {
      this.getStatus()
      this.getInfo()
    }, 1000)
  },
};
</script>

<style lang="scss" scoped>
.engine-button {
  padding: 0 8px!important;
  background-color: var(--v-success-base)!important;
  border-radius: 6px;
  box-shadow: none;
  color: white;
  font-size: 14px;
  text-transform: capitalize;

  ::v-deep {
    .v-btn__content {
      gap: 4px;
    }

    path {
      fill: white;
    }
  }
}

.offline {
  background-color: var(--v-grey-lighten-4)!important;
  color: var(--v-error-base);

  .event-name-header {
    color: var(--v-grey-lighten-1);
  }

  ::v-deep {
    path {
      fill: var(--v-error-base);
    }
  }
}

.event-name {
  padding: 0 10px;
  background-color: var(--v-blue-lighten-4);
  border-radius: 6px;
  color: var(--v-blue-darken-1);
  line-height: 24px;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.menu-wrapper {
  min-width: 354px!important;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 16px;
  padding-bottom: 12px;
  background-color: white;
  border-radius: 6px;
  box-shadow: 0px 5px 5px rgba(var(--v-primary-base-rgb), 0.1),
              0px 9px 10px rgba(var(--v-primary-base-rgb), 0.07),
              0px 5px 14px rgba(var(--v-primary-base-rgb), 0.06);

  button {
    border-radius: 6px;
    box-shadow: none;
    border: 1px solid var(--v-blue-lighten-2);
    background-color: white!important;
    color: var(--v-secondary-base);
    font-weight: 400;
    text-transform: capitalize;
  }
}

.menu-header {
  display: grid;
  grid-template-columns: 102px 1fr;
  column-gap: 20px;
  row-gap: 8px;
}

.menu-title {
  font-weight: 500;
  font-size: 18px;
  align-self: center;
  line-height: 24px;
}

.menu-event-name {
  background-color: var(--v-blue-lighten-4);
  border-radius: 6px;
  border: 1px solid var(--v-blue-lighten-1);
  color: var(--v-blue-lighten-1);
  padding: 3px 10px;
  text-align: center;
}

.menu-row {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  font-size: 14px;
  line-height: 24px;
}

.styled-cell {
  color: var(--v-grey-lighten-2);
  font-size: 12px;
  line-height: 16px;
}

.option-buttons {
  display: flex;
  gap: 8px;
}

.option-button-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  gap: 8px;

  button {
    width: 100%;
    height: 40px!important;
  }
}

.stop {
  color: #F48B7C;
}

.reboot {
  color: var(--v-blue-lighten-2);

  ::v-deep {
    path {
      stroke: var(--v-primary-base);
    }
  }
}

.divider {
  background: var(--v-blue-lighten-1);
  opacity: 0.5;
}
</style>
